/**
 * RecentProgramsNav (DEV-732) — the sidebar's recent-programs list. Each program
 * row shows title, days-since-touched, and its current/next step, and folds out
 * (chevron) into the program's workflow steps with a per-step status dot. The
 * step list is lazy-loaded from the graph endpoint the first time a program is
 * expanded (and auto-expands for the program you're currently inside), so we
 * never fetch every program's steps up front. Capped at the 10 most recent.
 */
import { useEffect, useRef, useState } from 'react'
import { NavLink, useMatch } from 'react-router-dom'
import { ChevronRight, Loader2 } from 'lucide-react'
import {
  workflowsApi,
  statusDotClass,
  statusLabel,
  type RecentProgram,
  type WorkflowStepNode,
} from '../services/workflows'

function daysSince(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const days = Math.floor((Date.now() - then) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return '1 day ago'
  return `${days} days ago`
}

function ProgramRow({ program }: { program: RecentProgram }) {
  const match = useMatch('/programs/:programId/*')
  const isActiveProgram = match?.params.programId === String(program.id)

  const [manualOpen, setManualOpen] = useState(false)
  const [steps, setSteps] = useState<WorkflowStepNode[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  // Guards a single fetch per row — kept in a ref (not the dep array) so that
  // the `setLoading(true)` re-render can't re-run the effect and cancel its own
  // in-flight request.
  const startedRef = useRef(false)

  // The program you're currently viewing is always expanded so you can see
  // where you are; otherwise it's driven by the chevron toggle.
  const expanded = manualOpen || isActiveProgram

  // Lazy-load steps the first time this row is expanded (either way).
  useEffect(() => {
    if (!expanded || startedRef.current) return
    startedRef.current = true
    let cancelled = false
    setLoading(true)
    workflowsApi
      .getGraph(program.id)
      .then((g) => { if (!cancelled) setSteps(g.nodes) })
      .catch(() => {
        if (!cancelled) {
          setFailed(true)
          startedRef.current = false // allow a retry on next expand
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [expanded, program.id])

  return (
    <div>
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={() => setManualOpen((v) => !v)}
          aria-label={expanded ? 'Collapse steps' : 'Expand steps'}
          aria-expanded={expanded}
          className="flex shrink-0 items-center justify-center px-1.5 text-muted-foreground hover:text-foreground"
        >
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </button>
        <NavLink
          to={`/programs/${program.id}`}
          end
          className={({ isActive }) =>
            `min-w-0 flex-1 rounded-lg px-2 py-2 text-sm transition-colors ${
              isActive
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-sidebar-foreground hover:bg-sidebar-accent/60'
            }`
          }
        >
          <div className="truncate font-medium">{program.name}</div>
          <div className="mt-0.5 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span className="truncate">
              {program.current_step ? program.current_step.name : 'Not started'}
            </span>
            <span className="shrink-0">{daysSince(program.updated_at)}</span>
          </div>
        </NavLink>
      </div>

      {expanded && (
        <div className="mb-1 ml-3 space-y-0.5 border-l border-sidebar-border pl-2">
          {loading && (
            <div className="flex items-center gap-2 px-2 py-1.5 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading steps…
            </div>
          )}
          {failed && (
            <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
              Couldn’t load steps.
            </div>
          )}
          {steps && steps.length === 0 && !loading && (
            <div className="px-2 py-1.5 text-[11px] text-muted-foreground">No steps yet.</div>
          )}
          {steps?.map((s) => (
            <NavLink
              key={s.step_key}
              to={`/programs/${program.id}/module/${s.step_key}`}
              title={statusLabel(s.status)}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors ${
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground/90 hover:bg-sidebar-accent/60'
                }`
              }
            >
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${statusDotClass(s.status)}`}
                aria-hidden
              />
              <span className="truncate">{s.name}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

export default function RecentProgramsNav() {
  const [programs, setPrograms] = useState<RecentProgram[]>([])

  useEffect(() => {
    let cancelled = false
    workflowsApi
      .recentPrograms(10)
      .then((r) => { if (!cancelled) setPrograms(r.programs) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  if (programs.length === 0) return null

  return (
    <div className="mt-2">
      <div className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Recent programs
      </div>
      <div className="space-y-0.5">
        {programs.map((p) => (
          <ProgramRow key={p.id} program={p} />
        ))}
      </div>
    </div>
  )
}
